import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Archive, Building2, Mail, Phone, Plus, Save, Search, UsersRound } from 'lucide-react';
import {
  companyContactStatusLabel,
  companyContactStatusTone,
  companyContactTypeLabel,
  splitTags,
  type CompanyContact,
  type CompanyContactStatus,
  type CompanyContactType,
  type CompanyPreferredContactMethod,
} from '../lib/companyContacts';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/PageTemplates';

type ContactFilter = CompanyContactStatus | 'all';

export function CompanyContactsPage() {
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ContactFilter>('active');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CompanyContact | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CompanyContact | null>(null);

  const load = () => {
    setLoading(true);
    fetch('/api/company/contacts', { credentials: 'same-origin' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        const data = (await res.json()) as { contacts: CompanyContact[] };
        setContacts(data.contacts);
      })
      .catch(() => setError('Could not load contacts.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return contacts.filter((contact) => {
      if (filter !== 'all' && contact.status !== filter) return false;
      if (!q) return true;
      return [
        contact.organization_name,
        contact.name,
        contact.email,
        contact.phone,
        contact.role,
        contact.tags,
        contact.notes,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [contacts, filter, query]);

  const counts = useMemo(
    () => ({
      all: contacts.length,
      active: contacts.filter((contact) => contact.status === 'active').length,
      inactive: contacts.filter((contact) => contact.status === 'inactive').length,
      archived: contacts.filter((contact) => contact.status === 'archived').length,
      leads: contacts.filter((contact) => contact.source === 'lead').length,
    }),
    [contacts],
  );

  const onArchive = async (contact: CompanyContact) => {
    if (contact.source === 'lead') return;
    setArchiveTarget(contact);
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    await fetch('/api/company/contacts', {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: archiveTarget.id, archive: true }),
    });
    setArchiveTarget(null);
    load();
  };

  return (
    <div className="stack" style={{ gap: 18 }}>
      <section className="card" style={{ padding: 24 }}>
        <div className="eyebrow">Scorekeeper HQ</div>
        <h1 style={{ margin: '6px 0 0', fontSize: 30 }}>Contacts</h1>
        <p className="muted" style={{ maxWidth: 760, marginTop: 10 }}>
          Lightweight company-side directory for sales leads, customer contacts,
          partners, and people tied to running The Scorekeeper.
        </p>
        <div className="row" style={{ gap: 10, marginTop: 18 }}>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus size={14} /> Add contact
          </button>
        </div>
      </section>

      <div className="grid grid--4">
        <Metric icon={UsersRound} label="All contacts" value={counts.all} hint={`${counts.leads} derived from leads`} />
        <Metric icon={Mail} label="Active" value={counts.active} hint="Reachable business contacts" />
        <Metric icon={Building2} label="Inactive" value={counts.inactive} hint="Dormant or lost leads" />
        <Metric icon={Archive} label="Archived" value={counts.archived} hint="Hidden from active work" />
      </div>

      <div className="card">
        <div className="directory-toolbar">
          <div className="directory-search">
            <Search size={14} aria-hidden />
            <input
              className="input"
              placeholder="Search contacts by league, person, email, phone, tag, or note..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="directory-chips">
            {(['active', 'inactive', 'archived', 'all'] as ContactFilter[]).map((status) => (
              <button
                key={status}
                type="button"
                className={`chip-btn ${filter === status ? 'is-active' : ''}`}
                onClick={() => setFilter(status)}
              >
                {status === 'all' ? 'All' : companyContactStatusLabel(status)}{' '}
                <span className="chip-btn__count">{counts[status]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading contacts...</p>
      ) : error ? (
        <p style={{ color: 'var(--err)' }}>{error}</p>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty">No contacts match this view.</div>
        </div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {filtered.map((contact) => (
            <article key={contact.id} className="card">
              <div className="row row--between" style={{ alignItems: 'flex-start', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="eyebrow">{companyContactTypeLabel(contact.contact_type)}</div>
                  <h3 className="card__title" style={{ marginTop: 4 }}>
                    {contact.organization_name || contact.name}
                  </h3>
                  <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
                    {contact.name}
                    {contact.role ? ` - ${contact.role}` : ''}
                  </div>
                  <div className="row" style={{ gap: 10, marginTop: 8, color: 'var(--text-muted)', fontSize: 13 }}>
                    {contact.email && <span className="directory-row__contact"><Mail size={12} /> {contact.email}</span>}
                    {contact.phone && <span className="directory-row__contact"><Phone size={12} /> {contact.phone}</span>}
                  </div>
                </div>
                <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                  <span className={`chip chip--${companyContactStatusTone(contact.status)}`}>
                    {companyContactStatusLabel(contact.status)}
                  </span>
                  <span className="chip">{contact.source === 'lead' ? 'Lead-derived' : 'Saved contact'}</span>
                </div>
              </div>
              <div className="row row--between" style={{ marginTop: 14, gap: 12 }}>
                <div className="row" style={{ gap: 6 }}>
                  {splitTags(contact.tags).map((tag) => <span key={tag} className="chip">{tag}</span>)}
                  {contact.lead_status && <span className="chip">Pipeline: {contact.lead_status.replace('_', ' ')}</span>}
                </div>
                <div className="row" style={{ gap: 8 }}>
                  {contact.source === 'contact' && (
                    <>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => {
                          setEditing(contact);
                          setOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      {contact.status !== 'archived' && (
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onArchive(contact)}>
                          <Archive size={12} /> Archive
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <ContactModal
        open={open}
        contact={editing}
        onClose={() => setOpen(false)}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />
      <ConfirmDialog
        open={Boolean(archiveTarget)}
        title={`Archive ${archiveTarget?.name ?? 'this contact'}?`}
        body="This hides the contact from active company-side work without deleting its history."
        confirmLabel="Archive contact"
        tone="danger"
        onCancel={() => setArchiveTarget(null)}
        onConfirm={() => {
          void confirmArchive();
        }}
      />
    </div>
  );
}

function ContactModal({
  open,
  contact,
  onClose,
  onSaved,
}: {
  open: boolean;
  contact: CompanyContact | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(() => contactForm(contact));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(contactForm(contact));
      setSaving(false);
      setError('');
    }
  }, [contact, open]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/company/contacts', {
        method: contact ? 'PATCH' : 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contact ? { ...form, id: contact.id } : form),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? `Save failed (${res.status})`);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save contact.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contact ? 'Edit contact' : 'Add contact'}
      subtitle="Company contacts stay separate from League HQ people."
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" form="company-contact-form" className="btn btn--primary" disabled={saving || !form.name.trim()}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save contact'}
          </button>
        </>
      }
    >
      <form id="company-contact-form" className="stack" onSubmit={onSubmit}>
          <div className="grid grid--2">
            <Field label="League / organization" value={form.organizationName} onChange={(organizationName) => setForm({ ...form, organizationName })} />
            <Field label="Name" value={form.name} onChange={(name) => setForm({ ...form, name })} required />
          </div>
          <div className="grid grid--2">
            <Field label="Email" value={form.email} onChange={(email) => setForm({ ...form, email })} />
            <Field label="Phone" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
          </div>
          <div className="grid grid--3">
            <Field label="Role" value={form.role} onChange={(role) => setForm({ ...form, role })} />
            <label className="field">
              <span className="field__label">Type</span>
              <select className="select" value={form.contactType} onChange={(event) => setForm({ ...form, contactType: event.target.value as CompanyContactType })}>
                <option value="manual">Manual</option>
                <option value="lead">Lead</option>
                <option value="customer">Customer</option>
                <option value="partner">Partner</option>
              </select>
            </label>
            <label className="field">
              <span className="field__label">Status</span>
              <select className="select" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as CompanyContactStatus })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </label>
          </div>
          <div className="grid grid--2">
            <label className="field">
              <span className="field__label">Preferred contact</span>
              <select className="select" value={form.preferredContactMethod} onChange={(event) => setForm({ ...form, preferredContactMethod: event.target.value as CompanyPreferredContactMethod })}>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="none">None</option>
              </select>
            </label>
            <Field label="Tags" value={form.tags} onChange={(tags) => setForm({ ...form, tags })} placeholder="demo, customer, east-coast" />
          </div>
          <label className="field">
            <span className="field__label">Notes</span>
            <textarea className="textarea" rows={4} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
          </label>
          {error && <p style={{ color: 'var(--err)', margin: 0 }}>{error}</p>}
      </form>
    </Modal>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof UsersRound;
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <div className="card">
      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
        <Icon size={15} color="var(--text-muted)" />
        <div className="stat__label">{label}</div>
      </div>
      <div className="stat__value" style={{ marginTop: 8 }}>{value}</div>
      <div className="stat__hint">{hint}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input className="input" value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} required={required} />
    </label>
  );
}

function contactForm(contact: CompanyContact | null) {
  return {
    organizationName: contact?.organization_name ?? '',
    name: contact?.name ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    role: contact?.role ?? '',
    contactType: contact?.contact_type ?? 'manual',
    status: contact?.status ?? 'active',
    preferredContactMethod: contact?.preferred_contact_method ?? 'email',
    tags: contact?.tags ?? '',
    notes: contact?.notes ?? '',
  };
}
